import {
  ApplicationConfig,
  afterNextRender,
  provideZoneChangeDetection,
  importProvidersFrom,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideServiceWorker } from '@angular/service-worker';
import { HttpClient, provideHttpClient, withFetch } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  BASE_URL_PROVIDER,
  ENVIRONMENT_PROVIDERS,
  IconRegistryService,
  LOCAL_IMAGE_PROCESSING_ENABLED,
  LocalizationService,
  DeviceDetectorService,
  provideAppVersion,
  SEO_CONFIG,
  SeoService,
} from '@imagise/shared';
import { provideImagiseCloudApiClient } from '@imagise/generated-imagise-cloud-api-client';
import { ERROR_REPORT_FEEDBACK_AUTH, provideErrorReporting } from '@imagise/features';
import { environment } from '../environments';
import { ANALYTICS_TRACKING_CONFIG, AppAnalyticsService } from '@imagise/features/analytics-consent';
import { IMAGISE_APP_ANALYTICS_TRACKING_CONFIG } from './analytics/analytics-tracking.config';
import { AuthService } from './auth/auth.service';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => inject(AuthService).initialize()),
    provideHttpClient(withFetch()),
    provideImagiseCloudApiClient({ basePath: environment.baseApiUrl }),
    provideClientHydration(withEventReplay()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      })
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !environment.isLocal,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      provide: ANALYTICS_TRACKING_CONFIG,
      useValue: IMAGISE_APP_ANALYTICS_TRACKING_CONFIG,
    },
    provideAppInitializer(() => {
      const appAnalytics = inject(AppAnalyticsService);

      inject(IconRegistryService).init();
      inject(DeviceDetectorService);
      inject(SeoService).init();
      afterNextRender(() => appAnalytics.init(environment.googleAnalyticsId));
    }),
    {
      provide: SEO_CONFIG,
      useValue: {
        baseUrl: environment.siteUrl,
        supportedLangs: ['en', 'ru', 'es', 'fr', 'zh'],
        defaultLang: 'en',
        siteName: 'Imagise',
        defaultImage: '/icons/icon-512x512.png',
      },
    },
    ENVIRONMENT_PROVIDERS,
    // Displayed in the footer; bump on release.
    provideAppVersion('1.0.0'),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: 'en',
      })
    ),
    {
      provide: LocalizationService,
      useClass: LocalizationService,
    },
    {
      provide: BASE_URL_PROVIDER,
      useValue: environment.baseApiUrl,
    },
    {
      provide: LOCAL_IMAGE_PROCESSING_ENABLED,
      useValue: environment.localImageProcessingEnabled,
    },
    provideErrorReporting({
      appName: 'imagise-app',
      isProduction: environment.isProduction,
      errorRoutePath: '/error',
    }),
    {
      provide: ERROR_REPORT_FEEDBACK_AUTH,
      useFactory: (auth: AuthService) => ({
        isAuthenticated: () => !!auth.user(),
        getAccessToken: () => auth.getAccessToken(),
      }),
      deps: [AuthService],
    },
  ],
};
